import { useCallback, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router';
import type { PaginationState, SortingState } from '@tanstack/react-table';

/** Filter values per key. Every filter holds a list, so single and multi select share one shape. */
export type ListFilters<K extends string> = Partial<Record<K, string[]>>;

export interface ListUrlStateConfig<TScope extends string, TFilterKey extends string> {
  /** Prefix for every param, for a page that keeps two lists in one URL (`a_q`, `b_q`). */
  prefix?: string;
  /** Page size when the URL names none. Default 25, the table default. */
  defaultPageSize?: number;
  /** Allowed `scope` values, e.g. `['current', 'all']`. An unknown value falls back to `defaultScope`. */
  scopes?: readonly TScope[];
  /** Scope when the URL names none. Required when `scopes` is given. */
  defaultScope?: TScope;
  /** Filter keys kept in the URL. Params outside this list are never read or written. */
  filterKeys?: readonly TFilterKey[];
  /** Sort when the URL names none; it is not written to the URL. */
  defaultSort?: SortingState;
}

export interface ListUrlState<TScope extends string, TFilterKey extends string> {
  search: string;
  setSearch: (search: string) => void;
  /** Zero-based page index, as TanStack Table counts. The URL shows it one-based. */
  page: number;
  setPage: (page: number) => void;
  pageSize: number;
  setPageSize: (pageSize: number) => void;
  /** `page` and `pageSize` in the shape `BaseEditableTable` takes as `pagination`. */
  pagination: PaginationState;
  /** Fits `BaseEditableTable`'s `onPaginationChange`. */
  setPagination: (
    updater: PaginationState | ((old: PaginationState) => PaginationState)
  ) => void;
  sort: SortingState;
  setSort: (sort: SortingState) => void;
  scope: TScope | undefined;
  setScope: (scope: TScope) => void;
  filters: ListFilters<TFilterKey>;
  setFilters: (
    next:
      | ListFilters<TFilterKey>
      | ((prev: ListFilters<TFilterKey>) => ListFilters<TFilterKey>)
  ) => void;
  /** Search, a filter or a non-default scope is set. Feeds `hasExternalFilters` and a reset control. */
  isFiltered: boolean;
  /**
   * Clears search, scope, filters and page. Keeps sort and page size, which say
   * how to show the list, not what is in it. Params this hook does not own stay.
   */
  reset: () => void;
  /**
   * Changes when the list params change from outside the list's own setters:
   * a link or the sidebar opens the same list with other params, Back/Forward,
   * or `reset`. State seeded once from the URL (the table's column filters)
   * re-seeds by using it as a React `key`.
   */
  syncKey: number;
}

const DEFAULT_PAGE_SIZE = 25;

function serializeSort(sort: SortingState): string {
  return sort.map(({ id, desc }) => (desc ? `-${id}` : id)).join(',');
}

function parseSort(raw: string | null): SortingState | null {
  if (!raw) return null;
  const sort = raw
    .split(',')
    .filter((part) => part !== '' && part !== '-')
    .map((part) =>
      part.startsWith('-') ? { id: part.slice(1), desc: true } : { id: part, desc: false }
    );
  return sort.length > 0 ? sort : null;
}

function parsePositiveInt(raw: string | null): number | null {
  if (!raw || !/^\d+$/.test(raw)) return null;
  const value = Number(raw);
  return value > 0 ? value : null;
}

/**
 * List state (search, page, page size, sort, scope, filters) kept in the URL
 * search params, so a filtered list survives a reload, can be shared as a link,
 * and comes back as it was when the user returns from a record with Back.
 *
 * Every write uses `replace`: refining a list is not a navigation, and Back
 * should leave the list, not step through each keystroke. Changing search,
 * sort, scope, filters or page size returns to the first page. Values equal to
 * their default are left out of the URL, so a fresh list has a clean URL.
 *
 * Params: `q`, `page` (one-based), `size`, `sort` (`name` or `-name`, comma
 * separated), `scope`, and one repeated param per filter key
 * (`status=active&status=paused`), each behind `prefix`.
 */
export function useListUrlState<TScope extends string = string, TFilterKey extends string = string>(
  config: ListUrlStateConfig<TScope, TFilterKey> = {}
): ListUrlState<TScope, TFilterKey> {
  const {
    prefix = '',
    defaultPageSize = DEFAULT_PAGE_SIZE,
    scopes,
    defaultScope,
    filterKeys,
    defaultSort,
  } = config;
  const [searchParams, setSearchParams] = useSearchParams();

  // react-router builds a functional update from the params of the last render,
  // so two writes in one event (the table resets the page while the page writes
  // the new search) would drop the first. Each write starts from the newest
  // params instead, including ones not yet rendered.
  const latestParamsRef = useRef(searchParams);
  latestParamsRef.current = searchParams;

  const keys = useMemo(
    () => ({
      q: `${prefix}q`,
      page: `${prefix}page`,
      size: `${prefix}size`,
      sort: `${prefix}sort`,
      scope: `${prefix}scope`,
      filter: (key: string) => `${prefix}${key}`,
    }),
    [prefix]
  );

  // Arrays from the caller are often inline literals; compare them by content.
  const filterKeysSignature = (filterKeys ?? []).join('\u0000');
  const scopesSignature = (scopes ?? []).join('\u0000');
  const defaultSortSignature = serializeSort(defaultSort ?? []);

  // The list's own params as one string, to tell a setter's write from a
  // change that came from elsewhere.
  const ownedSignature = useCallback(
    (params: URLSearchParams): string => {
      const filterParams = filterKeysSignature ? filterKeysSignature.split('\u0000').map(keys.filter) : [];
      return [keys.q, keys.page, keys.size, keys.sort, keys.scope, ...filterParams]
        .map((key) => `${key}=${params.getAll(key).join(',')}`)
        .join('&');
    },
    [keys, filterKeysSignature]
  );
  const signature = ownedSignature(searchParams);
  const renderedSignatureRef = useRef(signature);
  const ownWriteRef = useRef<string | null>(null);
  const syncKeyRef = useRef(0);
  if (signature !== renderedSignatureRef.current) {
    if (signature === ownWriteRef.current) ownWriteRef.current = null;
    else syncKeyRef.current += 1;
    renderedSignatureRef.current = signature;
  }

  const update = useCallback(
    (mutate: (params: URLSearchParams) => void, resetPage: boolean, own = true) => {
      const next = new URLSearchParams(latestParamsRef.current);
      mutate(next);
      if (resetPage) next.delete(keys.page);
      latestParamsRef.current = next;
      const nextSignature = ownedSignature(next);
      ownWriteRef.current = own && nextSignature !== renderedSignatureRef.current ? nextSignature : null;
      setSearchParams(next, { replace: true });
    },
    [keys, setSearchParams, ownedSignature]
  );

  const state = useMemo(() => {
    const search = searchParams.get(keys.q) ?? '';
    const pageNumber = parsePositiveInt(searchParams.get(keys.page));
    const page = pageNumber ? pageNumber - 1 : 0;
    const pageSize = parsePositiveInt(searchParams.get(keys.size)) ?? defaultPageSize;
    const sort = parseSort(searchParams.get(keys.sort)) ?? parseSort(defaultSortSignature) ?? [];

    const allowedScopes = scopesSignature ? scopesSignature.split('\u0000') : [];
    const rawScope = searchParams.get(keys.scope);
    const scope = (
      rawScope && allowedScopes.includes(rawScope) ? rawScope : defaultScope
    ) as TScope | undefined;

    const filters: ListFilters<TFilterKey> = {};
    const ownedKeys = filterKeysSignature ? filterKeysSignature.split('\u0000') : [];
    for (const key of ownedKeys) {
      const values = searchParams.getAll(keys.filter(key)).filter((value) => value !== '');
      if (values.length > 0) filters[key as TFilterKey] = values;
    }

    const isFiltered =
      search !== '' || Object.keys(filters).length > 0 || scope !== defaultScope;

    return { search, page, pageSize, sort, scope, filters, isFiltered };
  }, [searchParams, keys, defaultPageSize, defaultSortSignature, scopesSignature, defaultScope, filterKeysSignature]);

  const setSearch = useCallback(
    (search: string) =>
      update((params) => {
        if (search === '') params.delete(keys.q);
        else params.set(keys.q, search);
      }, true),
    [update, keys]
  );

  const setPage = useCallback(
    (page: number) =>
      update((params) => {
        if (page <= 0) params.delete(keys.page);
        else params.set(keys.page, String(page + 1));
      }, false),
    [update, keys]
  );

  const setPageSize = useCallback(
    (pageSize: number) =>
      update((params) => {
        if (pageSize === defaultPageSize) params.delete(keys.size);
        else params.set(keys.size, String(pageSize));
      }, true),
    [update, keys, defaultPageSize]
  );

  const setPagination = useCallback(
    (updater: PaginationState | ((old: PaginationState) => PaginationState)) => {
      const current: PaginationState = {
        pageIndex: (parsePositiveInt(latestParamsRef.current.get(keys.page)) ?? 1) - 1,
        pageSize: parsePositiveInt(latestParamsRef.current.get(keys.size)) ?? defaultPageSize,
      };
      const next = typeof updater === 'function' ? updater(current) : updater;
      update((params) => {
        if (next.pageSize === defaultPageSize) params.delete(keys.size);
        else params.set(keys.size, String(next.pageSize));
        // A new page size starts on the first page, as `setPageSize` does.
        const pageIndex = next.pageSize === current.pageSize ? next.pageIndex : 0;
        if (pageIndex <= 0) params.delete(keys.page);
        else params.set(keys.page, String(pageIndex + 1));
      }, false);
    },
    [update, keys, defaultPageSize]
  );

  const setSort = useCallback(
    (sort: SortingState) =>
      update((params) => {
        const serialized = serializeSort(sort);
        if (serialized === '' || serialized === defaultSortSignature) params.delete(keys.sort);
        else params.set(keys.sort, serialized);
      }, true),
    [update, keys, defaultSortSignature]
  );

  const setScope = useCallback(
    (scope: TScope) =>
      update((params) => {
        if (scope === defaultScope) params.delete(keys.scope);
        else params.set(keys.scope, scope);
      }, true),
    [update, keys, defaultScope]
  );

  const setFilters = useCallback(
    (
      next:
        | ListFilters<TFilterKey>
        | ((prev: ListFilters<TFilterKey>) => ListFilters<TFilterKey>)
    ) => {
      const ownedKeys = filterKeysSignature ? filterKeysSignature.split('\u0000') : [];
      const prev: ListFilters<TFilterKey> = {};
      for (const key of ownedKeys) {
        const values = latestParamsRef.current.getAll(keys.filter(key)).filter((v) => v !== '');
        if (values.length > 0) prev[key as TFilterKey] = values;
      }
      const resolved = typeof next === 'function' ? next(prev) : next;
      update((params) => {
        for (const key of ownedKeys) {
          params.delete(keys.filter(key));
          for (const value of resolved[key as TFilterKey] ?? []) {
            if (value !== '') params.append(keys.filter(key), value);
          }
        }
      }, true);
    },
    [update, keys, filterKeysSignature]
  );

  // Not an own write: the table did not ask for it, so its seeded filters must re-seed.
  const reset = useCallback(
    () =>
      update((params) => {
        [keys.q, keys.page, keys.scope].forEach((key) => params.delete(key));
        const ownedKeys = filterKeysSignature ? filterKeysSignature.split('\u0000') : [];
        ownedKeys.forEach((key) => params.delete(keys.filter(key)));
      }, true, false),
    [update, keys, filterKeysSignature]
  );

  const pagination = useMemo<PaginationState>(
    () => ({ pageIndex: state.page, pageSize: state.pageSize }),
    [state.page, state.pageSize]
  );

  return {
    ...state,
    setSearch,
    setPage,
    setPageSize,
    pagination,
    setPagination,
    setSort,
    setScope,
    setFilters,
    reset,
    syncKey: syncKeyRef.current,
  };
}
